import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';
import { resourceLoaderFactory } from './resource-loader.factory';
import { L10nTranslationService } from 'angular-l10n';
import { MiqSecuredResourcesConfig, SECURED_RESOURCES_CONFIGS_TOKEN } from './miq-secured-resources.token';

@Injectable()
export class MiqSecuredResourcesService {
  private _imageCache = {};
  resolverServiceWithByPassSecurity: Function;
  resolverService: Function;

  constructor(
    @Inject(SECURED_RESOURCES_CONFIGS_TOKEN) private securedResourcesConfig: MiqSecuredResourcesConfig,
    private http: HttpClient,
    domSanitizer: DomSanitizer,
    public translationService: L10nTranslationService
  ) {
    const bypassSecurityTrusted = e => domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(e));
    this.resolverServiceWithByPassSecurity = resourceLoaderFactory(
      this.http,
      bypassSecurityTrusted,
      securedResourcesConfig.userLSToken,
      this.securedResourcesConfig.baseUrl
    );

    const simpleBlobCreator = e => URL.createObjectURL(e);
    this.resolverService = resourceLoaderFactory(
      this.http,
      simpleBlobCreator,
      securedResourcesConfig.userLSToken,
      this.securedResourcesConfig.baseUrl
    );
  }

  loadImage(rawURI: string, params = { responseType: 'blob' }, prependS3Uri: boolean = false): Observable<Object> {
    let uri = prependS3Uri ? `${this.securedResourcesConfig.s3Path}${rawURI}` : rawURI;
    if (this._imageCache[uri]) {
      const isObservable = typeof this._imageCache[uri].subscribe === 'function';
      return isObservable ? this._imageCache[uri] : of(this._imageCache[uri]);
    }
    this._imageCache[uri] = this.resolverService(this.normalizeUri(uri), params).pipe(
      tap(response => (this._imageCache[uri] = response)),
      catchError(() => {
        this._imageCache[uri] = this.securedResourcesConfig.buildingPhName;
        throw new Error(this.translationService.translate('shared.couldNotRetrieveImage'));
      }),
      share()
    );
    return this._imageCache[uri];
  }

  downloadImage(rawURI: string, prependS3Uri: boolean = false) {
    const uri = prependS3Uri ? `${this.securedResourcesConfig.s3Path}${rawURI}` : rawURI;
    return this.http.get(this.normalizeUri(uri), { responseType: 'blob', observe: 'response' }).pipe(
      map(({ body }) => ({
        blob: body,
      })),
      catchError(() => {
        throw new Error(this.translationService.translate('shared.errorWhileDownloadImage'));
      })
    );
  }

  private _sanitizedImageCache: Record<string, any> = {};
  loadSanitizedImage(
    rawURI: string,
    params = { responseType: 'blob' },
    prependS3Uri: boolean = false
  ): Observable<Object> {
    const uri = prependS3Uri ? `${this.securedResourcesConfig.s3Path}${rawURI}` : rawURI;
    const key = this.normalizeUri(uri);
    if (this._sanitizedImageCache[key]) {
      const cached = this._sanitizedImageCache[key];
      return typeof cached.subscribe === 'function' ? cached : of(cached);
    }
    this._sanitizedImageCache[key] = this.resolverServiceWithByPassSecurity(key, params).pipe(
      tap((response: any) => (this._sanitizedImageCache[key] = response)),
      share()
    );
    return this._sanitizedImageCache[key];
  }

  normalizeUri(uri: string) {
    return uri.startsWith('/') ? uri : `/${uri}`;
  }
}
